import { prepareCloudEventPayload } from '~/utils/prepare-cloud-event-payload';
import { getClientFeedUrl } from '~/services/request';
import logger from '~/services/logger';
import type { ActionContext, ActionHandler } from '../types';
import { ActionResult } from '../enums';
import { getMergedClientContextKeys } from '~/utils/context-setter-storage.ts';

type SendCloudEvent = {
  url: string;
  type: string;
  subject: string;
  data?: Record<string, unknown>;
  id?: string;
};

type AnalyticsTrackData = {
  version?: string;
  sdk_version?: string;
};

type ActionContextData = {
  analyticsTrackData?: AnalyticsTrackData;
};

/**
 * Sends a CloudEvent through the client-feed API proxy.
 */
export const sendCloudEvent: ActionHandler<SendCloudEvent> = async (
  action: {
    type: string;
    options?: SendCloudEvent;
  },
  ctx: ActionContext
) => {
  if (!action.options) {
    logger.info('sendCloudEvent: missing options');
    return ActionResult.Error;
  }

  if (!action.options?.url) {
    logger.info('sendCloudEvent: missing url');
    return ActionResult.Error;
  }

  const contextData = ctx.data as ActionContextData | undefined;
  const version: string | undefined =
    contextData?.analyticsTrackData?.version ||
    contextData?.analyticsTrackData?.sdk_version;

  const source: string = version
    ? `${window.location.origin}/version/${version}`
    : window.location.origin;

  const payloadData: Record<string, unknown> = {
    ...(action.options?.data || {}),
    ...(action.options?.id ? { id: action.options.id } : {}),
  };

  const body = prepareCloudEventPayload({
    type: action.options.type,
    source,
    subject: action.options.subject,
    data: payloadData,
  });

  const mergedClientContextKeys = getMergedClientContextKeys();

  const clientFeedUrl: string = `/${getClientFeedUrl(
    action.options.url,
    Object.keys(mergedClientContextKeys).length > 0
      ? mergedClientContextKeys
      : undefined
  )}`;

  try {
    const response = await fetch(clientFeedUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      logger.info(`sendCloudEvent: response not ok: ${response.status}`);
      return ActionResult.Error;
    }

    return ActionResult.Success;
  } catch (error: unknown) {
    logger.error(`sendCloudEvent: request failed: ${error}`);
    return ActionResult.Error;
  }
};
